import React, { isValidElement, type MouseEvent, type ReactElement, type ReactNode } from "react";
import { IconCheckmark } from "@purpur/icon/checkmark";
import * as RadixToggleGroup from "@radix-ui/react-toggle-group";
import c from "classnames/bind";

import styles from "./chip-group-item.module.scss";

type StylingProps = Pick<React.HTMLAttributes<HTMLElement>, "style" | "className">;

type ChipGroupItemProps = StylingProps & {
  ["aria-label"]?: string;
  ["data-testid"]?: string;
  children: ReactNode;
  /**
   * When `true`, prevents the user from interacting with the item.
   * */
  disabled?: boolean;
  /**
   * This prop will be passed automatically from the parent component (ChipGroup) when `type` is `"filter"`.
   * */
  showCheckmark?: boolean;
  /**
   * A unique value for the item.
   * */
  value: string;
  /**
   * This prop will only be used when `type` is `"choice"`.
   * When `true`, we recommend using `aria-label` to announce to screen readers that the option is unavailable.
   * */
  unavailable?: boolean;
};

const cx = c.bind(styles);
export const rootClassName = "purpur-chip-group-item";

export const ChipGroupItem = ({
  ["aria-label"]: allyLabel,
  children,
  className,
  ["data-testid"]: dataTestId,
  disabled = false,
  showCheckmark = false,
  unavailable = false,
  value,
  ...props
}: ChipGroupItemProps) => {
  const classes = cx([
    rootClassName,
    {
      [`${rootClassName}--disabled`]: disabled,
      [`${rootClassName}--unavailable`]: unavailable,
    },
    className,
  ]);

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      e.preventDefault();
    }
  };

  return (
    <RadixToggleGroup.Item
      aria-label={allyLabel}
      data-testid={dataTestId}
      className={`${classes} ${rootClassName}`}
      value={value}
      onClick={handleClick}
      aria-disabled={disabled}
      {...props}
    >
      {children}
      {showCheckmark && (
        <IconCheckmark
          size="sm"
          className={cx(`${rootClassName}__checkmark-icon`)}
          data-testid="checkmark icon"
        />
      )}
      {unavailable && (
        <span
          className={cx(`${rootClassName}__line-wrapper`)}
          data-testid="unavailable line wrapper"
        />
      )}
    </RadixToggleGroup.Item>
  );
};

export const isChipGroupItem = (child?: ReactNode): child is ReactElement<ChipGroupItemProps> =>
  isValidElement<ChipGroupItemProps>(child) && child?.type === ChipGroupItem;
